import type { TicketStatus, TicketSummary } from '@nadoutong/contracts';
import type { StoredNotice } from './store.ts';

/**
 * Ticket-notice display heuristics — the layout the desktop client
 * finalized (2026-08-16 feedback rounds) ported to the website.
 *
 * Everything here operates on the DISPLAY TEXT of `title`/`description`
 * only, computed at render time; storage is never migrated. And the
 * #116 fingerprint contract holds throughout: no function in this file
 * reads or parses `fingerprint` — the `tk_` fragments matched below
 * live in display text, which merely happens to contain the same shape.
 *
 * The pipeline, per ticket-kind notice:
 *   1. `detectTicketNoticeStatus` — colorize by the status word in the
 *      server copy.
 *   2. `replaceTicketIdSegments` — swap `tk_` ids the tickets list can
 *      resolve for the ticket's display title (emphasized segments).
 *   3. `promoteTicketTitleNotice` — when a title was resolved, promote
 *      it to the heading line and keep only the remaining text
 *      ("状态变更为 X" / a reply body) underneath.
 *   4. Reply-shaped notices get a localized "Reply:" prefix so the body
 *      reads as a quote, not a status change.
 *
 * Every step degrades to the original text when recognition fails — an
 * invisible ticket, an empty index, or copy the word table does not
 * know must never leave a hole in a sentence.
 */

/**
 * Status word table. DEPENDS ON THE SERVER COPY LANGUAGE: the
 * `description` of `/v1/notifications` rows is rendered server-side and
 * is currently fixed Chinese (e.g. 「工单 tk_x 状态变更为「已解决」」),
 * regardless of the console locale — so only the Chinese words are
 * recognized. If the server copy ever localizes, this table grows with
 * it. No match → null → generic severity presentation.
 */
export const TICKET_STATUS_DISPLAY_WORDS: ReadonlyArray<
  readonly [word: string, status: TicketStatus]
> = [
  ['待处理', 'open'],
  ['处理中', 'processing'],
  ['已解决', 'resolved'],
  ['已关闭', 'closed'],
];

/**
 * Detect the ticket status a display text talks about. The LAST
 * occurrence wins: transition copy puts the target state at the end
 * (「由「待处理」变更为「处理中」」 must read as `processing`).
 */
export function detectTicketNoticeStatus(text: string): TicketStatus | null {
  let status: TicketStatus | null = null;
  let lastIndex = -1;
  for (const [word, candidate] of TICKET_STATUS_DISPLAY_WORDS) {
    const index = text.lastIndexOf(word);
    if (index > lastIndex) {
      lastIndex = index;
      status = candidate;
    }
  }
  return status;
}

export function isTicketNotice(notice: Pick<StoredNotice, 'kind'>): boolean {
  return notice.kind === 'ticket';
}

/**
 * Reply-shaped ticket notices, recognized by the server's display title
 * (currently 「工单有新回复」). Same discipline as the status table:
 * display-copy keyword only, unrecognized falls back to the plain
 * ticket presentation with no prefix.
 */
export function isTicketReplyNotice(title: string): boolean {
  return title.includes('有新回复');
}

// Server display text refers to tickets as `tk_<id>`. Used only to
// locate/replace fragments in DISPLAY text — unrelated to fingerprints.
const TICKET_ID_DISPLAY_RE = /tk_\w+/g;

/** Every `tk_` id mentioned by ticket notices (deduped). Empty = skip the lookup query. */
export function collectTicketNoticeIds(
  notices: ReadonlyArray<Pick<StoredNotice, 'kind' | 'title' | 'description'>>,
): readonly string[] {
  const ids = new Set<string>();
  for (const notice of notices) {
    if (!isTicketNotice(notice)) {
      continue;
    }
    for (const text of [notice.title, notice.description]) {
      for (const match of text.matchAll(TICKET_ID_DISPLAY_RE)) {
        ids.add(match[0]);
      }
    }
  }
  return [...ids];
}

/**
 * Tickets have no separate title field (#116); the tickets page treats
 * the description's first line as the title, and notices follow suit,
 * truncated to fit inside one line of notice copy.
 */
export const TICKET_DISPLAY_TITLE_MAX = 24;

export function ticketDisplayTitle(description: string): string {
  const firstLine =
    description
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line.length > 0) ?? '';
  if (firstLine.length <= TICKET_DISPLAY_TITLE_MAX) {
    return firstLine;
  }
  return `${firstLine.slice(0, TICKET_DISPLAY_TITLE_MAX)}…`;
}

/**
 * id → display title index from a tickets-list response. A ticket the
 * index is missing (invisible to this role, deleted, endpoint down)
 * simply falls back to its raw id in the copy.
 */
export function buildTicketTitleIndex(
  tickets: readonly TicketSummary[],
): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  for (const ticket of tickets) {
    const title = ticketDisplayTitle(ticket.description);
    if (ticket.id.length > 0 && title.length > 0 && !index.has(ticket.id)) {
      index.set(ticket.id, title);
    }
  }
  return index;
}

export interface NoticeTextSegment {
  readonly text: string;
  /** true = a substituted ticket title; rendered emphasized. */
  readonly emphasized: boolean;
}

/**
 * Replace resolvable `tk_` id fragments with the ticket's display
 * title. Unresolvable ids stay verbatim — better to show an id than to
 * leave a gap in the sentence. Display-only; nothing is written back.
 */
export function replaceTicketIdSegments(
  text: string,
  titles: ReadonlyMap<string, string>,
): readonly NoticeTextSegment[] {
  const segments: NoticeTextSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(TICKET_ID_DISPLAY_RE)) {
    const at = match.index ?? 0;
    const title = titles.get(match[0]);
    if (title === undefined) {
      continue;
    }
    if (at > cursor) {
      segments.push({ text: text.slice(cursor, at), emphasized: false });
    }
    segments.push({ text: title, emphasized: true });
    cursor = at + match[0].length;
  }
  if (cursor < text.length || segments.length === 0) {
    segments.push({ text: text.slice(cursor), emphasized: false });
  }
  return segments;
}

/** Segments back to plain text (hover titles use the same substitution result). */
export function noticeSegmentsPlainText(segments: readonly NoticeTextSegment[]): string {
  return segments.map((segment) => segment.text).join('');
}

/**
 * Title promotion (desktop 2026-08-16 final): when the description
 * resolved a ticket title, the heading line becomes that title (the
 * generic 「工单状态更新」 heading is dropped) and the body keeps only
 * the text AFTER the title segment, with the leading separator
 * (colon/comma/whitespace) stripped. Examples:
 *   「工单 <标题> 状态变更为「已关闭」」 → title=<标题>, body=「状态变更为「已关闭」」
 *   「工单 <标题>:已处理」               → title=<标题>, body=「已处理」
 * No resolvable title in the description → everything stays as is.
 */
export function promoteTicketTitleNotice(
  title: readonly NoticeTextSegment[],
  description: readonly NoticeTextSegment[],
): {
  readonly title: readonly NoticeTextSegment[];
  readonly description: readonly NoticeTextSegment[];
  /** Whether promotion happened — reply prefixes apply only then. */
  readonly promoted: boolean;
} {
  const at = description.findIndex((segment) => segment.emphasized);
  const hit = at >= 0 ? description[at] : undefined;
  if (hit === undefined) {
    return { title, description, promoted: false };
  }
  const rest = [...description.slice(at + 1)];
  const first = rest[0];
  if (first !== undefined) {
    rest[0] = { ...first, text: first.text.replace(/^[\s:：,，·]+/, '') };
  }
  const body = rest.filter((segment) => segment.text.length > 0);
  return {
    title: [hit],
    // An empty remainder (description was just 「工单 <标题>」) falls
    // back to the original heading line — never an empty body row.
    description: body.length > 0 ? body : title,
    promoted: true,
  };
}
